/**
 * Page de gestion des fermetures du boulanger
 * Permet de définir les périodes de fermeture pour gérer les précommandes
 */
import { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, AlertCircle } from 'lucide-react';
import { api } from '../services/api';
import { useTenant } from '../contexts/TenantContext';

interface BakerClosure {
  id: string;
  start_date: string;
  end_date: string;
  reason?: string;
  created_at: string;
}

export default function BakerClosures() {
  const { tenant } = useTenant();
  const [closures, setClosures] = useState<BakerClosure[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  // Formulaire
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (tenant?.id) {
      loadClosures();
    }
  }, [tenant]);

  const loadClosures = async () => {
    setLoading(true);
    try {
      const response = await api.get('/preorders/baker-closures/list', {
        headers: { 'X-Tenant-ID': tenant!.id },
      });
      setClosures(response || []);
    } catch (err) {
      console.error('Erreur chargement fermetures:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!startDate || !endDate) {
      alert('Les dates de début et fin sont obligatoires');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert('La date de fin doit être après la date de début');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(
        '/preorders/baker-closures',
        {
          start_date: startDate,
          end_date: endDate,
          reason: reason.trim() || null,
        },
        { headers: { 'X-Tenant-ID': tenant!.id } }
      );

      alert('✅ Fermeture ajoutée avec succès');
      setStartDate('');
      setEndDate('');
      setReason('');
      setShowAddModal(false);
      loadClosures();
    } catch (err: any) {
      alert(`❌ Erreur: ${err.response?.data?.message || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteClosure = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette fermeture ?')) {
      return;
    }

    try {
      await api.delete(`/preorders/baker-closures/${id}`, {
        headers: { 'X-Tenant-ID': tenant!.id },
      });
      alert('✅ Fermeture supprimée');
      loadClosures();
    } catch (err: any) {
      alert(`❌ Erreur: ${err.response?.data?.message || err.message}`);
    }
  };

  const isClosureActive = (closure: BakerClosure) => {
    const now = new Date();
    const start = new Date(closure.start_date);
    const end = new Date(closure.end_date);
    return now >= start && now <= end;
  };

  const isClosureFuture = (closure: BakerClosure) => {
    const now = new Date();
    const start = new Date(closure.start_date);
    return start > now;
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">🗓️ Fermetures du boulanger</h1>
        <p className="text-gray-600">
          Gérez les périodes de fermeture de Jean-Sébastien pour adapter les précommandes
        </p>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 flex-shrink-0 mt-1" size={24} />
          <div>
            <h3 className="font-bold text-blue-900 text-lg mb-2">Comment ça fonctionne ?</h3>
            <ul className="space-y-1 text-blue-800 text-sm">
              <li>• Les fermetures définies ici empêcheront les précommandes pour les dates concernées</li>
              <li>• Le système reportera automatiquement les précommandes à la prochaine date disponible</li>
              <li>• Les clients seront informés si leur date souhaitée n'est pas disponible</li>
              <li>• Pensez à ajouter les fermetures prévues (vacances, jours fériés, etc.)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bouton ajouter */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
        >
          <Plus size={20} />
          Ajouter une fermeture
        </button>
      </div>

      {/* Modal ajout */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="border-b px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">Nouvelle fermeture</h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Date début */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Date de début *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Date fin */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Date de fin *
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Raison */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Raison (optionnel)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: Vacances d'été, congés annuels..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setStartDate('');
                    setEndDate('');
                    setReason('');
                  }}
                  className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-300 font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold text-white ${
                    submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {submitting ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Liste des fermetures */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      ) : closures.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune fermeture définie</h3>
          <p className="text-gray-600">Ajoutez les périodes de fermeture du boulanger</p>
        </div>
      ) : (
        <div className="space-y-4">
          {closures
            .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
            .map((closure) => {
              const isActive = isClosureActive(closure);
              const isFuture = isClosureFuture(closure);
              const isPast = !isActive && !isFuture;

              return (
                <div
                  key={closure.id}
                  className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
                    isActive
                      ? 'border-red-500 bg-red-50'
                      : isFuture
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-300 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900">
                          {new Date(closure.start_date).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                          {' → '}
                          {new Date(closure.end_date).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </h3>
                        {isActive && (
                          <span className="px-3 py-1 bg-red-600 text-white rounded-full text-sm font-semibold">
                            🔴 En cours
                          </span>
                        )}
                        {isFuture && (
                          <span className="px-3 py-1 bg-orange-600 text-white rounded-full text-sm font-semibold">
                            ⏰ À venir
                          </span>
                        )}
                        {isPast && (
                          <span className="px-3 py-1 bg-gray-400 text-white rounded-full text-sm font-semibold">
                            ✓ Passée
                          </span>
                        )}
                      </div>

                      {closure.reason && (
                        <p className="text-gray-700 font-medium">💬 {closure.reason}</p>
                      )}

                      <p className="text-sm text-gray-500 mt-2">
                        Durée: {Math.ceil((new Date(closure.end_date).getTime() - new Date(closure.start_date).getTime()) / (1000 * 60 * 60 * 24)) + 1} jour(s)
                      </p>
                    </div>

                    <button
                      onClick={() => deleteClosure(closure.id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
